import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { ErrorHandler, Injectable } from '@angular/core';
import { CookieService } from "ngx-cookie-service";
import { Utils } from "ng/src/app/core/utils";
import { Subject } from 'rxjs';
import { throttleTime } from 'rxjs/operators';
import { CustomErrorProperties, LoggedErrorResponse } from '../../shared/model/error.model';
import { LogBookService } from './logbook.service';
import { PrimeNGHelpersService } from './primeng-helpers.service';

export type SupportedErrorType = Error | HttpErrorResponse;
export type CustomErrorPropertyOptions<K extends keyof CustomErrorProperties> = {[U in K]: CustomErrorProperties[U]}
type ErrorType = SupportedErrorType & CustomErrorProperties;

@Injectable({
	providedIn: 'root'
})
export class ErrorService implements ErrorHandler {
	private static lastErrorMessage?: string
	private errorSubject = new Subject<ErrorType>();

	constructor(
		private http: HttpClient,
		private primeNGHelpers: PrimeNGHelpersService,
		private logBookService: LogBookService,
		private cookieService: CookieService
	) {
		this.errorSubject.pipe(
			throttleTime(5000)
		)
		.subscribe(this.showAndLogError);
	}

	handleError(error: ErrorType) {
		this.errorSubject.next(error);
	}
	
	handleStandardError<K extends keyof CustomErrorProperties>(error: SupportedErrorType, properties?: CustomErrorPropertyOptions<K>) {
		const preparedError = Utils.let(error as ErrorType, (casted) => {
			if (properties) {
				Object.entries(properties).forEach(entry => {
					casted[entry[0] as K] = entry[1] as ErrorType[K];
				})
			}
			return casted;
		});
		
		this.handleError(preparedError);
	}
	
	private showAndLogError = (error: ErrorType) => {
		try {
			const suppressError = this.cookieService.get('suppress-error-toast') == 'true' || !!(error.suppressErrorToast);
			
			if (!suppressError) {
				this.primeNGHelpers.showErrorToast(error.message, error.toastTitle, error.sticky);
			}

			console.error(error);

			if (error.message == ErrorService.lastErrorMessage || error.noLog)
				return;
			
			ErrorService.lastErrorMessage = error.message;
			
			let loggedError = '';
			
			if (error instanceof HttpErrorResponse) {
				// Server Error
				const responseMessage = error.error.message ? `\nResponse Message: ${error.error.message}` : '';
				
				loggedError = `SERVER ERROR\nStatus Code: ${error.status}\nMessage: ${error.message}${responseMessage}\nURL: ${error.url || 'N/A'}`;
			}
			else {
				// Client Error
				loggedError = `CLIENT ERROR\nName: ${error.name}\nMessage: ${error.message}\nStack: ${error.stack || 'N/A'}`;
			}

			this.http.post<LoggedErrorResponse>('/api/loggedErrors', {
				url: document.location.href,
				message: `${loggedError}\n\nBuild Number: ${this.logBookService.LOGBOOK_API_VERSION}`
			}).subscribe();
		}
		catch(e) {
			console.error(e);
		}
	}
}